import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { useContext, useEffect, useState } from 'react';
import styles from './CarDetail.module.css';
import { useTranslation } from 'react-i18next';
import {
	CarDTO,
	CarImageDTO,
	CustomCarImageResourceService,
	CustomCarResourceService,
	CustomTemporaryDataResourceService,
	TemporaryDataDTO
} from '@/openapi';
import Loader from '@/components/UI/Loader';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import { useNavigate, useParams } from 'react-router-dom';
import InputField from '@/components/UI/InputField';
import Button from '@/components/UI/Button';
import { ReactComponent as ConsumptionSVG } from '@Assets/svg/car_avgConsuption.svg';
import { ReactComponent as EuroSVG } from '@Assets/svg/euro.svg';
import { ReactComponent as Co2SVG } from '@Assets/svg/car_co2.svg';
import { ReactComponent as DoorSVG } from '@Assets/svg/car_door.svg';
import { ReactComponent as MoreDetailSVG } from '@Assets/svg/car_moreDetail.svg';
import { ReactComponent as EngineSVG } from '@Assets/svg/car_engine.svg';
import { ReactComponent as FuelTypeSVG } from '@Assets/svg/car_fuelType.svg';
import { ReactComponent as HorsePowerSVG } from '@Assets/svg/car_horsePower.svg';
import { ReactComponent as KmSVG } from '@Assets/svg/car_km.svg';
import { ReactComponent as DriveTypeSVG } from '@Assets/svg/car_driveType.svg';
import { ReactComponent as TransmissionSVG } from '@Assets/svg/car_transmission.svg';
import { ReactComponent as MainDetailSVG } from '@Assets/svg/car_detail.svg';
import { ReactComponent as DocumentSVG } from '@Assets/svg/document.svg';
import { ReactComponent as ImageSVG } from '@Assets/svg/image.svg';
import { ReactComponent as NoImageSVG } from '@Assets/svg/no_image.svg';
import { ReactComponent as PrevSVG } from '@Assets/svg/prev.svg';
import { ReactComponent as NextSVG } from '@Assets/svg/next.svg';
import { ReactComponent as BackSVG } from '@Assets/svg/back.svg';
import { ReactComponent as CarServicesSVG } from '@Assets/svg/carServices.svg';
import { ReactComponent as CaskoSVG } from '@Assets/svg/casko.svg';
import { ReactComponent as FireSVG } from '@Assets/svg/fire.svg';
import { ReactComponent as InjurySVG } from '@Assets/svg/injury.svg';
import { ReactComponent as MaintenanceSVG } from '@Assets/svg/maintenance.svg';
import { ReactComponent as RcaSVG } from '@Assets/svg/rca.svg';
import { ReactComponent as TowTruckSVG } from '@Assets/svg/tow-truck.svg';
import Divider from '@/components/Footer/Divider';
import { useRemotePost } from '@/hooks/useRemotePost';
import { AuthContext } from '@/contexts/AuthContext';
import SuccessToast from '@/components/UI/SuccessToast';
import { formatPriceWithThousandsSeparator } from '@/utils/utils';

const CarDetail = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const authCtx = useContext(AuthContext);
	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');

	const { carId } = useParams<{ carId: string }>();
	const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

	const [signCommission, setSignCommission] = useState<boolean>(false);

	// Form state
	const [formData, setFormData] = useState<Partial<TemporaryDataDTO>>({
		firstname: '',
		lastname: '',
		email: '',
		phone: '',
		whatsapp: '',
		completeAddress: '',
		town: '',
		province: '',
		ownerContact: '',
		taxCode: '',
		nrVAT: '',
		companyName: '',
		marketingContactStatus: '',
		textNote: ''
	});

	const [successToast, setSuccessToast] = useState<{
		isVisible: boolean;
		message: string;
		type: 'success' | 'error';
	}>({
		isVisible: false,
		message: '',
		type: 'success'
	});

	const showToast = (message: string, isError: boolean = false) => {
		setSuccessToast({
			isVisible: true,
			message,
			type: isError ? 'error' : 'success'
		});
	};

	const getCar = useRemoteGet<CarDTO>({
		remoteMethod: async () => {
			if (!carId) throw new Error('Car ID not available to get details');
			return CustomCarResourceService.getOffer({ carId: parseInt(carId) });
		},
		lazy: true
	});

	const getCarImages = useRemoteGet<CarImageDTO[]>({
		remoteMethod: async () => {
			if (!carId) throw new Error('Car ID not available to get images');
			return CustomCarImageResourceService.getAllCarImages({ carId: parseInt(carId) });
		},
		lazy: true
	});

	const sendTempData = useRemotePost<TemporaryDataDTO, { requestBody: TemporaryDataDTO }>({
		remoteMethod: CustomTemporaryDataResourceService.createTemporaryHsData,
		lazy: true
	});

	useEffect(() => {
		if (!carId) {
			navigate('/catalog');
			return;
		}

		getCar.fetch();
		getCarImages.fetch();
	}, [carId]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmitRequest = (e?: React.MouseEvent<HTMLButtonElement>) => {
		e?.preventDefault();

		console.log('Form data:', formData);

		if (isRoleAdmin) {
			showToast(t('carDetail.form.adminNotAllowed'), true);
			return;
		}

		if (sendTempData.error) {
			sendTempData.resetError(); // Reset dell'errore
		}

		// Validazione campi obbligatori
		if (!formData.firstname?.trim() || formData.firstname?.trim().length < 2 || formData.firstname?.trim().length > 50) {
			showToast(t('carDetail.form.firstnameLength'), true);
			return;
		}

		if (!formData.lastname?.trim() || formData.lastname?.trim().length < 2 || formData.lastname?.trim().length > 50) {
			showToast(t('carDetail.form.lastnameLength'), true);
			return;
		}

		if (!formData.email?.trim()) {
			showToast(t('carDetail.form.emailRequired'), true);
			return;
		} else {
			const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
			if (!emailRegex.test(formData.email) || formData.email.length > 100) {
				showToast(t('carDetail.form.emailInvalid'), true);
				return;
			}
		}

		// Validazione campi opzionali
		if (formData.phone?.trim() && formData.phone?.trim().length > 20) {
			showToast(t('carDetail.form.phoneLength'), true);
			return;
		}

		if (formData.whatsapp?.trim() && formData.whatsapp?.trim().length > 20) {
			showToast(t('carDetail.form.whatsappLength'), true);
			return;
		}

		if (formData.completeAddress?.trim() && formData.completeAddress?.trim().length > 255) {
			showToast(t('carDetail.form.addressLength'), true);
			return;
		}

		if (
			formData.companyName?.trim() &&
			(formData.companyName?.trim().length < 2 || formData.companyName?.trim().length > 50)
		) {
			showToast(t('carDetail.form.companyNameLength'), true);
			return;
		}

		const tempData: TemporaryDataDTO = {
			...formData,
			carId: getCar.data?.id || 0,
			signallerId: authCtx.userId || 0,
			leadType: formData.nrVAT?.trim() ? 'PIVA' : 'Privato'
		} as TemporaryDataDTO;

		sendTempData.fetch({ requestBody: tempData });
	};

	useEffect(() => {
		if (sendTempData.data && !sendTempData.loading) {
			showToast(t('carDetail.form.successMessage'), false);
			// Reset del form dopo invio riuscito
			setFormData({
				firstname: '',
				lastname: '',
				email: '',
				phone: '',
				whatsapp: '',
				completeAddress: '',
				town: '',
				province: '',
				ownerContact: '',
				taxCode: '',
				nrVAT: '',
				companyName: '',
				marketingContactStatus: '',
				textNote: ''
			});
		}

		if (sendTempData.error && !sendTempData.loading) {
			showToast(sendTempData.errorDetail || t('carDetail.form.errorMessage'), true);
		}
	}, [sendTempData.data, sendTempData.error]);

	if (!carId) {
		return null;
	}

	const services = [
		{
			key: 'fullCoverage',
			label: t('carDetails.services.fullCoverage'),
			description: t('carDetails.services.fullCoverageDescription'),
			icon: <CaskoSVG className={styles.svg} />,
			included: getCar.data?.fullCoverage
		},
		{
			key: 'liabilityInsurance',
			label: t('carDetails.services.liabilityInsurance'),
			description: t('carDetails.services.liabilityInsuranceDescription'),
			icon: <RcaSVG className={styles.svg} />,
			included: getCar.data?.liabilityInsurance
		},
		{
			key: 'fireTheft',
			label: t('carDetails.services.fireTheft'),
			description: t('carDetails.services.fireTheftDescription'),
			icon: <FireSVG className={styles.svg} />,
			included: getCar.data?.fireTheft
		},
		{
			key: 'driverInjury',
			label: t('carDetails.services.driverInjury'),
			description: t('carDetails.services.driverInjuryDescription'),
			icon: <InjurySVG className={styles.svg} />,
			included: getCar.data?.driverInjury
		},
		{
			key: 'maintenance',
			label: t('carDetails.services.maintenance'),
			description: t('carDetails.services.maintenanceDescription'),
			icon: <MaintenanceSVG className={styles.svg} />,
			included: getCar.data?.maintenance
		},
		{
			key: 'roadsideAssistance',
			label: t('carDetails.services.roadsideAssistance'),
			description: t('carDetails.services.roadsideAssistanceDescription'),
			icon: <TowTruckSVG className={styles.svg} />,
			included: getCar.data?.roadsideAssistance
		}
	];

	return (
		<div className={styles.main_home_container}>
			<Navbar className={styles.navDivider} />

			<main className={styles.mainContent}>
				<Loader visible={getCar.loading || getCarImages.loading || sendTempData.loading} />

				<SuccessToast
					isVisible={successToast.isVisible}
					message={successToast.message}
					type={successToast.type}
					duration={5000}
					onClose={() => setSuccessToast((prev) => ({ ...prev, isVisible: false }))}
				/>

				{getCar.data && !getCar.loading && (
					<>
						{/* Badge Offerta */}
						<section className={styles.heroSection}>
							<div className={styles.heroContainer}>
								<div className={styles.breadcrumb}>
									<button className={styles.backButton} onClick={() => navigate('/catalog')}>
										<BackSVG className={styles.svg} />
										{t('common.back')}
									</button>
									<span className={styles.breadcrumbSeparator}>•</span>
									<span className={styles.breadcrumbCurrent}>{t('carDetail.breadcrumb.current')}</span>
								</div>

								<div className={styles.heroContent}>
									<div className={styles.carInfo}>
										<h1 className={styles.carTitle}>
											{getCar.data.brand} {getCar.data.model}
										</h1>
										<div className={styles.carBadges}>
											<span className={styles.categoryBadge}>{getCar.data.category}</span>
											{getCar.data.productionYear !== null && getCar.data.productionYear !== undefined && (
												<span className={styles.yearBadge}>{getCar.data.productionYear}</span>
											)}
										</div>
									</div>

									<div className={styles.containerPriceSection}>
										<div className={styles.priceSection}>
											{getCar.data.category === 'RENTAL' && (
												<div className={styles.priceCard}>
													<div className={styles.priceMain}>
														<div className={styles.priceAmount}>
															€ {formatPriceWithThousandsSeparator(getCar.data.pricePerMonth?.toFixed(2))}
														</div>
													</div>
													<div className={styles.priceTotal}>{t('carDetail.hero.perMonth')}</div>
												</div>
											)}

											<div className={styles.priceCard}>
												{getCar.data.category === 'RENTAL' ? (
													<div className={styles.priceMain}>
														<span className={styles.priceAmount}>{t('carDetail.hero.months', { months: getCar.data.months })}</span>
													</div>
												) : (
													<div className={styles.priceTotal}>
														<span className={styles.priceAmount}>{`${formatPriceWithThousandsSeparator(
															getCar.data.totalPrice?.toFixed(2)
														)} €`}</span>
													</div>
												)}

												{getCar.data.advance !== null && getCar.data.advance !== undefined && (
													<div className={styles.priceTotal}>
														{t('carDetail.hero.advance', {
															advance: formatPriceWithThousandsSeparator(getCar.data.advance.toFixed(2))
														})}
													</div>
												)}
											</div>
										</div>
										<div className={styles.vat}>{t('carDetails.ivaMessage')}</div>
									</div>
								</div>
							</div>
						</section>

						{/* Specifiche Offerta */}
						<section className={styles.contentSection}>
							<div className={styles.contentGrid}>
								<div className={styles.specsGrid}>
									{/* Card Galleria immagini */}
									<div className={styles.galleryCard}>
										<div className={styles.cardHeader}>
											<h2 className={styles.cardTitle}>
												<ImageSVG className={styles.svg} />
												{t('carDetail.gallery.title')}
											</h2>
										</div>

										<div className={styles.imageGallery}>
											{getCarImages.data && getCarImages.data.length > 0 ? (
												<>
													<div className={styles.mainImageContainer}>
														<div className={styles.imgContainer}>
															<img
																src={`data:${getCarImages.data[currentImageIndex].srcContentType};base64,${getCarImages.data[currentImageIndex].src}`}
																alt={`${getCar.data.brand} ${getCar.data.model}`}
																className={styles.mainImage}
															/>
														</div>

														{getCarImages.data.length > 1 && (
															<>
																<button
																	className={`${styles.navButton} ${styles.prevButton}`}
																	onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? getCarImages.data!.length - 1 : prev - 1))}
																>
																	<PrevSVG className={styles.svg} />
																</button>
																<button
																	className={`${styles.navButton} ${styles.nextButton}`}
																	onClick={() => setCurrentImageIndex((prev) => (prev === getCarImages.data!.length - 1 ? 0 : prev + 1))}
																>
																	<NextSVG className={styles.svg} />
																</button>
															</>
														)}

														<div className={styles.imageCounter}>
															{currentImageIndex + 1} / {getCarImages.data.length}
														</div>
													</div>

													{getCarImages.data.length > 1 && (
														<div className={styles.thumbnailGrid}>
															{getCarImages.data.map((image, index) => (
																<button
																	key={index}
																	className={`${styles.thumbnail} ${index === currentImageIndex ? styles.activeThumbnail : ''}`}
																	onClick={() => setCurrentImageIndex(index)}
																>
																	<img src={`data:${image.srcContentType};base64,${image.src}`} alt={`Thumbnail ${index + 1}`} />
																</button>
															))}
														</div>
													)}
												</>
											) : (
												<div className={styles.noImage}>
													<NoImageSVG className={styles.svg} />
													<p>{t('carDetail.gallery.noImage')}</p>
												</div>
											)}
										</div>
									</div>

									<div className={styles.specsCard} onClick={() => setSignCommission(!signCommission)}>
										<div className={styles.cardHeader}>
											<h2 className={styles.cardTitle}>
												<MainDetailSVG className={styles.svg} />
												{t('carDetail.specs.signaller')}
											</h2>
										</div>

										{signCommission && (
											<div className={styles.specsItems}>
												{getCar.data.agentCommission !== null &&
													getCar.data.agentCommission !== undefined &&
													authCtx.closedContracts !== null &&
													authCtx.closedContracts !== undefined && (
														<div className={styles.specItem}>
															<div className={styles.specIcon}>
																<EuroSVG className={styles.svg} />
															</div>
															<div>
																<span className={styles.specLabel}>{t('carDetail.specs.commission')}</span>
																<span className={styles.specValue}>
																	{(getCar.data.agentCommission + (authCtx.closedContracts > 3 ? 100 : 0)).toLocaleString()} €
																</span>
															</div>
														</div>
													)}
											</div>
										)}
									</div>
								</div>

								{/* Card Specifiche tecniche */}
								<div className={styles.specsGrid}>
									<div className={styles.specsCard}>
										<div className={styles.cardHeader}>
											<h2 className={styles.cardTitle}>
												<MainDetailSVG className={styles.svg} />
												{t('carDetail.specs.main')}
											</h2>
										</div>

										<div className={styles.specsItems}>
											{getCar.data.km !== null && getCar.data.km !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<KmSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.mileage')}</span>
														<span className={styles.specValue}>{getCar.data.km.toLocaleString()} km</span>
													</div>
												</div>
											)}

											{getCar.data.fuelType !== null && getCar.data.fuelType !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<FuelTypeSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.fuel')}</span>
														<span className={styles.specValue}>{getCar.data.fuelType}</span>
													</div>
												</div>
											)}

											{getCar.data.horsepower !== null && getCar.data.horsepower !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<HorsePowerSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.power')}</span>
														<span className={styles.specValue}>{getCar.data.horsepower} CV</span>
													</div>
												</div>
											)}

											{getCar.data.transmission !== null && getCar.data.transmission !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<TransmissionSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.transmission')}</span>
														<span className={styles.specValue}>{getCar.data.transmission}</span>
													</div>
												</div>
											)}

											{getCar.data.driveType !== null && getCar.data.driveType !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<DriveTypeSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.driveType')}</span>
														<span className={styles.specValue}>{getCar.data.driveType}</span>
													</div>
												</div>
											)}
										</div>
									</div>

									<div className={styles.specsCard}>
										<div className={styles.cardHeader}>
											<h2 className={styles.cardTitle}>
												<MoreDetailSVG className={styles.svg} />
												{t('carDetail.specs.additional')}
											</h2>
										</div>

										<div className={styles.specsItems}>
											{getCar.data.doors !== null && getCar.data.doors !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<DoorSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.doors')}</span>
														<span className={styles.specValue}>{getCar.data.doors}</span>
													</div>
												</div>
											)}

											{getCar.data.averageConsumption !== null && getCar.data.averageConsumption !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<ConsumptionSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.consumption')}</span>
														<span className={styles.specValue}>{getCar.data.averageConsumption} L/100km</span>
													</div>
												</div>
											)}

											{getCar.data.co2Emissions !== null && getCar.data.co2Emissions !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<Co2SVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.co2')}</span>
														<span className={styles.specValue}>{getCar.data.co2Emissions} g/km</span>
													</div>
												</div>
											)}

											{getCar.data.engineDisplacement !== null && getCar.data.engineDisplacement !== undefined && (
												<div className={styles.specItem}>
													<div className={styles.specIcon}>
														<EngineSVG className={styles.svg} />
													</div>
													<div>
														<span className={styles.specLabel}>{t('carDetail.specs.engineDisplacement')}</span>
														<span className={styles.specValue}>{getCar.data.engineDisplacement} cc</span>
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* SERVICES */}
							<div className={styles.servicesSection}>
								<div className={styles.cardHeader}>
									<h2 className={styles.cardTitle}>
										<CarServicesSVG className={styles.svg} />
										{t('carDetails.services.title')}
									</h2>
								</div>

								{(() => {
									const includedServices = services.filter((service) => service.included);
									const count = includedServices.length;

									// Determina la classe CSS per la griglia in base al numero di servizi
									const gridClass =
										count >= 5
											? styles.gridCols3
											: count === 4 || count === 3 || count === 2
											? styles.gridCols2
											: styles.gridCols1;

									if (count === 0) {
										return null;
									}

									return (
										<div className={`${styles.servicesGrid} ${gridClass}`}>
											{includedServices.map((service) => (
												<div key={service.key} className={styles.serviceCard}>
													<div className={styles.border_glow}></div>
													<div className={styles.card_inner_content}>
														<div className={styles.carhHeader}>
															<div className={styles.specIcon}>{service.icon}</div>
															{service.label}
														</div>
														<div className={styles.cardContent}>{service.description}</div>
													</div>
												</div>
											))}
										</div>
									);
								})()}
							</div>
						</section>

						{/* FORM */}
						{isRoleAdmin || (
							<div className={styles.formSection}>
								<div className={styles.formContainer}>
									<div className={styles.formHeader}>
										<h2 className={styles.formTitle}>
											<DocumentSVG className={styles.svg} />
											{t('carDetail.form.title')}
										</h2>
										<p className={styles.formSubtitle}>{t('carDetail.form.subtitle')}</p>
									</div>

									<form className={styles.requestForm}>
										<div className={styles.formGrid}>
											<div className={styles.formRow}>
												<InputField
													name="firstname"
													type="text"
													placeholder={t('carDetail.form.firstname.placeholder')}
													title={t('carDetail.form.firstname.title')}
													label={t('carDetail.form.firstname.label')}
													required={true}
													value={formData.firstname}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
												<InputField
													name="lastname"
													type="text"
													placeholder={t('carDetail.form.lastname.placeholder')}
													title={t('carDetail.form.lastname.title')}
													label={t('carDetail.form.lastname.label')}
													required={true}
													value={formData.lastname}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
											</div>

											<div className={styles.formRow}>
												<InputField
													name="email"
													type="email"
													placeholder={t('carDetail.form.email.placeholder')}
													title={t('carDetail.form.email.title')}
													label={t('carDetail.form.email.label')}
													required={true}
													value={formData.email}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
												<InputField
													name="phone"
													type="tel"
													placeholder={t('carDetail.form.phone.placeholder')}
													title={t('carDetail.form.phone.title')}
													label={t('carDetail.form.phone.label')}
													required={false}
													value={formData.phone}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
											</div>

											<div className={styles.formRow}>
												<InputField
													name="province"
													type="text"
													placeholder={t('carDetail.form.province.placeholder')}
													title={t('carDetail.form.province.title')}
													label={t('carDetail.form.province.label')}
													required={false}
													value={formData.province}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
												<InputField
													name="whatsapp"
													type="tel"
													placeholder={t('carDetail.form.whatsapp.placeholder')}
													title={t('carDetail.form.whatsapp.title')}
													label={t('carDetail.form.whatsapp.label')}
													required={false}
													value={formData.whatsapp}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
											</div>

											{/*
											<div className={styles.formRow}>
												<InputField
													name="town"
													type="text"
													placeholder={t('carDetail.form.town.placeholder')}
													title={t('carDetail.form.town.title')}
													label={t('carDetail.form.town.label')}
													required={false}
													value={formData.town}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
												<InputField
													name="taxCode"
													type="text"
													placeholder={t('carDetail.form.taxCode.placeholder')}
													title={t('carDetail.form.taxCode.title')}
													label={t('carDetail.form.taxCode.label')}
													required={false}
													value={formData.taxCode}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
											</div>

											<InputField
												name="completeAddress"
												type="text"
												placeholder={t('carDetail.form.completeAddress.placeholder')}
												title={t('carDetail.form.completeAddress.title')}
												label={t('carDetail.form.completeAddress.label')}
												required={false}
												value={formData.completeAddress}
												onChange={handleInputChange}
												labelClassName={styles.inputLabel}
											/>

											<div className={styles.formRow}>
												<InputField
													name="companyName"
													type="text"
													placeholder={t('carDetail.form.companyName.placeholder')}
													title={t('carDetail.form.companyName.title')}
													label={t('carDetail.form.companyName.label')}
													required={false}
													value={formData.companyName}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
												<InputField
													name="nrVAT"
													type="text"
													placeholder={t('carDetail.form.nrVAT.placeholder')}
													title={t('carDetail.form.nrVAT.title')}
													label={t('carDetail.form.nrVAT.label')}
													required={false}
													value={formData.nrVAT}
													onChange={handleInputChange}
													labelClassName={styles.inputLabel}
												/>
											</div>
											*/}

											<InputField
												name="textNote"
												type="text"
												placeholder={t('carDetail.form.textNote.placeholder')}
												title={t('carDetail.form.textNote.title')}
												label={t('carDetail.form.textNote.label')}
												required={false}
												value={formData.textNote}
												onChange={handleInputChange}
												labelClassName={styles.inputLabel}
											/>
										</div>

										<div className={styles.formActions}>
											<Button
												name={t('carDetail.form.submit')}
												onClick={handleSubmitRequest}
												color="black"
												background="black"
												className={styles.submitButton}
											/>
										</div>
									</form>
								</div>
							</div>
						)}
					</>
				)}
				<Divider className={styles.footer_divider} />
			</main>
			<Footer />
		</div>
	);
};

export default CarDetail;
